/**
 * /{ws}/docs — every page and database I can open in one flat, searchable list (core DocsViewModel over the page
 * tree's cache), filtered All / Recent / Created by me / Favorites / Databases, most recently edited first. A row opens
 * the page or database; "New page" creates one in my private pages (PageTreeViewModel.create).
 */
import { DocsViewModel, Shell } from "@bundle/core";
import type { DocsFilter } from "@bundle/core/logic/docsList";
import { DOCS_FILTERS } from "@bundle/core/logic/docsList";
import { Button, Icon, IconButton, ListRow, ScrollView, SegmentedControl, Spinner, Stack, Text, TextField, useFormat, useNavigation, useOwned, usePageTreeViewModel, useT, useViewModel, useWorkspace } from "@bundle/ui";
import { useCallback } from "react";

export function DocsScreen() {
  const t = useT();
  const fmt = useFormat();
  const nav = useNavigation();
  const ws = useWorkspace();
  const tree = usePageTreeViewModel();
  const make = useCallback(() => new DocsViewModel(ws.vm, tree), [ws.vm, tree]);
  const vm = useOwned(`docsList:${ws.vm.wsId}`, make);
  const s = useViewModel(vm);
  const create = async () => {
    const page = await tree.create({});
    if (page !== undefined) nav.open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: page.id }));
  };
  const filters = DOCS_FILTERS.map((f: DocsFilter) => ({ value: f, label: t(`docs.filter.${f}`) }));
  return (
    <Stack grow testID="docs-screen">
      <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="6" paddingY="4">
        <Text variant="title2" weight="bold">
          {t("docs.title")}
        </Text>
        {s.canCreatePages ? <Button icon="pagePlus" label={t("docs.new")} onPress={() => void create()} testID="docs-new" /> : null}
      </Stack>
      <Stack paddingX="6" gap="3">
        <TextField value={s.query} onChange={(q) => vm.setQuery(q)} placeholder={t("docs.search")} accessibilityLabel={t("docs.search")} leadingIcon="search" testID="docs-search" />
        <SegmentedControl<DocsFilter> options={filters} value={s.filter} onChange={(f) => vm.setFilter(f)} variant="pills" size="sm" accessibilityLabel={t("docs.title")} testID="docs-filter" />
      </Stack>
      {s.status === "loading" && s.rows.length === 0 ? (
        <Stack grow align="center" justify="center">
          <Spinner />
        </Stack>
      ) : s.rows.length === 0 ? (
        <Stack grow align="center" justify="center" gap="2" padding="8" testID="docs-empty">
          <Icon name="page" size="icon.lg" color="text.tertiary" />
          <Text color="text.secondary" align="center">
            {t("docs.empty")}
          </Text>
        </Stack>
      ) : (
        <ScrollView testID="docs-list">
          <Stack paddingX="4" paddingY="2" gap="0.5">
            {s.rows.map(({ page, path, favorite }) => {
              const url = Shell.formatUrl(page.kind === "database" ? { type: "db", ws: ws.slug, dbId: page.id } : { type: "page", ws: ws.slug, pageId: page.id });
              const emoji = page.icon !== undefined && !page.icon.startsWith("icon:") ? page.icon : undefined;
              const edited = t("docs.edited", { when: fmt.timestamp(page.updatedAt) });
              return (
                <ListRow
                  key={page.id}
                  title={page.title === "" ? t("page.untitled") : page.title}
                  subtitle={path === "" ? edited : `${path} · ${edited}`}
                  leading={emoji === undefined ? <Icon name={page.kind === "database" ? "database" : "page"} size="icon.md" color="text.secondary" /> : <Text>{emoji}</Text>}
                  trailing={
                    <IconButton
                      icon={favorite ? "star" : "starOff"}
                      size="sm"
                      accessibilityLabel={favorite ? t("docs.menu.removeFavorite") : t("docs.menu.addFavorite")}
                      onPress={() => tree.setFavorite(page.id, !favorite)}
                      testID={`docs-fav-${page.id}`}
                    />
                  }
                  onPress={() => nav.open(url)}
                  testID={`docs-row-${page.id}`}
                />
              );
            })}
          </Stack>
        </ScrollView>
      )}
    </Stack>
  );
}
